<template>
  <div>
    <div class="form-fields">
      <fieldset>
        <legend>Contractor Information</legend>
        <div class="row">
          <div class="col-12 col-sm-6 col-lg">
            <label class="required">{{form.projectType | capitalize}} Contractor Name</label>
            <vue-simple-suggest :list="contractors"
                                mode="input"
                                :placeholder="capitalize(form.projectType) + ' Contractor Name'"
                                value-attribute="id"
                                display-attribute="name"
                                :filter-by-query="true"
                                @select="contractorSelected">
              <div class="input-group">
                <!--One-Way binding (asbestos only)-->
                <input type="text" class="form-control" v-if="contractorReadonly" :value="form.contractor.name" />
                <!--One-Way binding (not asbestos) - with existing contractor selected (id)-->
                <input type="text" class="form-control" v-else-if="form.contractor.id" :value="form.contractor.name" />
                <!--Two-Way binding (not asbestos) - for new contractor only-->
                <input type="text" class="form-control" v-else v-model="form.contractor.name" />
                <div class="input-group-append" v-if="form.contractor.id && !contractorReadonly">
                  <button class="btn btn-outline-secondary" @click="clearContractor">
                    I Am A New Contractor
                  </button>
                </div>
              </div>
            </vue-simple-suggest>
            <!-- Asbestos only -->
            <small class="form-text text-muted" v-if="contractorReadonly">
              Enter the name of the Asbestos Abatement contractor and select from the drop-down list.  If the contractor is not present they need to register with the department.
              The contractor registration can be submitted <a href="/DesWebPortal/#/contractor" target="_blank">here</a>.
            </small>
            <!-- Not Asbestos -->
            <small class="form-text text-muted" v-else>
              Select your contractor from the list. If you do not see your contractor name please enter the contractor
              information to create a new demolition contractor.
            </small>
          </div>
          <!-- Asbestos only -->
          <div class="col-12 col-sm-6 col-lg" v-if="contractorReadonly">
            <base-input v-model="form.contractor.contractorIDPH"
                        :readonly="contractorReadonly"
                        label="IDPH Number"/>
          </div>
        </div>

        <hr />

        <v-address v-model="form.contractor.address"
                   :readonly="(contractorReadonly) || (!contractorReadonly && form.contractor.id)"
                   :validation="val.form.contractor.address"
                   labelPrefix="Contractor"/>
        <hr />
        <div class="row">
          <div class="col-12 col-sm-6 col-lg">
            <base-input v-model="form.contractorContactFirstName"
                        label="Project Contact First Name"
                        placeholder="First Name"
                        :validation="val.form.contractorContactFirstName"/>
          </div>
          <div class="col-12 col-sm-6 col-lg">
            <base-input v-model="form.contractorContactLastName"
                        label="Project Contact Last Name"
                        placeholder="Last Name"
                        :validation="val.form.contractorContactLastName"/>
          </div>
          <div class="col-12 col-sm-6 col-lg">
            <base-input v-model="form.contractorContactPhone"
                        label="Project Contact Phone"
                        type="tel"
                        placeholder="(xxx)-xxx-xxxx"
                        mask="(###)-###-####"
                        :validation="val.form.contractorContactPhone"/>
          </div>
          <div class="col-12 col-sm-6 col-lg">
            <base-input v-model="form.contractorContactEmail"
                        label="Project Contact Email"
                        type="email"
                        placeholder="name@company.com"
                        :validation="val.form.contractorContactEmail"/>
          </div>
        </div>
      </fieldset>
      <fieldset>
        <legend>Variance Information</legend>
        <div class="row">
          <div class="col-9">
            <label>
              Does the current situation pose an imminent health and safety risk to the public?
            </label>
          </div>
          <div class="col">
            <yes-no v-model="form.varianceCheckPublicHealth"/>
          </div>
        </div>

        <div class="row">
          <div class="col-9">
            <label>
              Has a sudden unplanned event occurred that would cause equipment failure or an unreasonable financial burden?
            </label>
          </div>
          <div class="col">
            <yes-no v-model="form.varianceCheckFinancialBurden"/>
          </div>
        </div>

        <div class="row">
          <div class="col-9">
            <label>
              Are there official documents from a town/village/municipality in the form of an
              order related to the condition of the structure? Are there official documents from a court in the form of an order?
            </label>
          </div>
          <div class="col">
            <yes-no v-model="form.varianceCheckCourtOrder"
                    :validation="val.form.varianceCheckCourtOrder" />
          </div>
        </div>

        <div class="row" v-if="isVariance">
          <div class="col">
            <base-checkbox v-model="form.varianceNoRefundAcknowledgement"
                           label="I understand that by answering yes to any of these questions, I will be charged a
                           variance fee and that fee is not refundable."
                           message="Field must be checked."
                           :validation="val.form.varianceNoRefundAcknowledgement"/>
          </div>
          <div class="w-100"></div>
          <div class="col">
            <base-input v-model="form.varianceNotesPublicHealth"
                        maxlength="500"
                        label="Explain:"
                        placeholder="Explanation"
                        :validation="val.form.varianceNotesPublicHealth"/>
          </div>
          <div class="w-100"></div>
          <div class="col">
            <label>
              When requesting a variance, you must submit documentation supporting the emergency. Sample documentation includes:
            </label>
            <ul>
              <li>Letters from property owners</li>
              <li>Letters from contractors</li>
              <li>Letters from villages/municipalities/town officials</li>
              <li>Court orders from every level</li>
            </ul>
          </div>
          <div class="col-12 col-sm-6 col-lg">
            <base-file-uploader v-model="form.files.varianceHealthRisk"
                                label="Supporting Documentation"
                                :validation="val.form.files.asbestosAssessmentFile"
                                :file="form.existingFileDetails.varianceHealthRisk"/>
          </div>
        </div>

      </fieldset>
      <fieldset>
        <legend>Project Information</legend>
        <div class="row">
          <div class="col-9">
            <label>
              Has the work already been started (or is complete)?
            </label>
          </div>
          <div class="col">
            <yes-no v-model="form.varianceCheckWorkStartedorComplete" />
          </div>
        </div>
        <div class="row" v-if="form.varianceCheckWorkStartedorComplete">
          <div class="col-2">
            <label class="required">
              Explain:
            </label>
          </div>
          <div class="col">
            <base-input v-model="form.varianceNotesWorkStartedorComplete"
                        :validation="val.form.varianceNotesWorkStartedorComplete"
                        maxlength="500"
                        placeholder="Explanation" />
          </div>
        </div>
        <div class="row">
          <div class="col-12 col-md-6">
            <base-date-picker v-model="form.startDate"
                              label="Start Date"
                              :validation="val.form.startDate" />
          </div>
          <div class="col-12 col-md-6">
            <base-date-picker v-model="form.endDate"
                              label="End Date"
                              :validation="val.form.endDate" />
          </div>
        </div>
        <label>Work Hours</label>
        <div class="row">
          <div class="col-12 col-md-6">
            <base-time-picker v-model="form.workHours.workHoursStart"
                              label="Start Time"
                              default-am-pm="AM"
                              :validation="val.form.workHours.workHoursStart" />
          </div>
          <div class="col-12 col-md-6">
            <base-time-picker v-model="form.workHours.workHoursEnd"
                              default-am-pm="PM"
                              :validation="val.form.workHours.workHoursEnd"
                              label="End Time" />
          </div>
        </div>
        <div class="row">
          <div class="col-12 col-md-6">
            <yes-no v-model="form.workHours.workWeekends"
                    label="Working Weekends?" />
          </div>
          <div class="col-12 col-md-6" v-if="form.projectType.toLowerCase() === 'demolition'">
            <base-select v-model="form.partialFullDemo"
                         :options="partialFullDemoOptions"
                         :validation="val.form.partialFullDemo"
                         label="Type of Demolition">
            </base-select>
          </div>
        </div>
        <div v-if="form.projectType.toLowerCase() === 'asbestos' && form.facility && form.facility.type === 'School'">
          <hr />
          <div class="row">
            <div class="col-12 col-sm-6 col-lg">
              <base-input v-model="form.projectDesignerId"
                          label="Project Designer ID #"
                          maxlength="25"
                          :validation="val.form.projectDesignerId" />
            </div>
            <div class="col-12 col-sm-6 col-lg">
              <base-input v-model="form.asbestosProjectManagerId"
                          label="Asbestos Project Manager ID #"
                          maxlength="25"
                          :validation="val.form.asbestosProjectManagerId" />
            </div>
            <div class="col-12 col-sm-6 col-lg">
              <base-input v-model="form.airSamplingProfessionalId"
                          label="Air Sampling Professional ID #"
                          maxlength="25"
                          :validation="val.form.airSamplingProfessionalId" />
            </div>
            <div class="w-100"></div>
            <div class="col-12 col-sm-6 col-lg">
              <base-input v-model="form.projectDesignerName"
                          label="Project Designer Name"
                          maxlength="25"
                          :validation="val.form.projectDesignerName" />
            </div>
            <div class="col-12 col-sm-6 col-lg">
              <base-input v-model="form.asbestosProjectManagerName"
                          label="Asbestos Project Manager Name"
                          maxlength="25"
                          :validation="val.form.asbestosProjectManagerName" />
            </div>
            <div class="col-12 col-sm-6 col-lg">
              <base-input v-model="form.airSamplingProfessionalName"
                          label="Air Sampling Professional Name"
                          maxlength="25"
                          :validation="val.form.airSamplingProfessionalName" />
            </div>
          </div>
        </div>
        <hr>
        <base-textarea v-model="form.specialCircumstances"
                       label="Special Notes to Consider (Use this space to explain in detail things related to this project,
                       such as specific site location, accessibility concerns, etc.) :"
                       :validation="val.form.specialCircumstances" />

        <hr>
        <base-input v-model="form.transporter.name"
                    label="Waste Transporter Company Name"
                    :validation="val.form.transporter.name" />
        <div class="row">
          <div class="col-12 col-sm-6 col-lg">
            <base-input v-model="form.transporter.contact"
                        label="Waste Transporter Contact Name"
                        :validation="val.form.transporter.contact" />
          </div>
          <div class="col-12 col-sm-6 col-lg">
            <base-input v-model="form.transporter.phone"
                        mask="(###)-###-####"
                        label="Waste Transporter Phone"
                        :validation="val.form.transporter.phone" />
          </div>
          <div class="col-12 col-sm-6 col-lg">
            <base-input v-model="form.transporter.email"
                        label="Waste Transporter Site Email"
                        :validation="val.form.transporter.email" />
          </div>
        </div>
        <hr />

        <v-address v-model="form.transporter.address"
                   :validation="val.form.transporter.address"
                   labelPrefix="Transporter" />
        <hr />
        <base-input v-model="form.disposalSite.name"
                    label="Disposal Site/Landfill Company Name"
                    :validation="val.form.disposalSite.name" />
        <div class="row">
          <div class="col-12 col-sm-6 col-lg">
            <base-input v-model="form.disposalSite.contact"
                        label="Disposal Site Contact Name"
                        :validation="val.form.disposalSite.contact" />
          </div>
          <div class="col-12 col-sm-6 col-lg">
            <base-input v-model="form.disposalSite.phone"
                        mask="(###)-###-####"
                        label="Disposal Site Phone"
                        :validation="val.form.disposalSite.phone" />
          </div>
          <div class="col-12 col-sm-6 col-lg">
            <base-input v-model="form.disposalSite.email"
                        label="Disposal Site Email"
                        :validation="val.form.disposalSite.email" />
          </div>
        </div>
        <hr />

        <v-address v-model="form.disposalSite.address"
                   :validation="val.form.disposalSite.address"
                   labelPrefix="Disposal Site" />
        <hr />
      </fieldset>
    </div>
    <bottom-nav :link="form.projectType.toLowerCase() === 'demolition' ? 'materials' : 'asbestos'">
      {{form.projectType.toLowerCase() === 'demolition' ? 'Materials' : 'Asbestos Information'}}
    </bottom-nav>
  </div>
</template>

<script>
import BaseInput from '~/components/BaseInput';
import ExplodedFieldset from '~/components/ExplodedFieldset';
import VAddress from '~/components/VAddress';
import BottomNav from '~/components/BottomNavigation';
import BaseTimePicker from '~/components/BaseTimePicker';
import BaseDatePicker from '~/components/BaseDatePicker';
import BaseCheckbox from '~/components/BaseCheckbox';
import BaseTextarea from '~/components/BaseTexarea';
import YesNo from '~/components/YesNo';
import { getContractors, capitalize } from '~/js/FormFunctions';
import { CapitalizeFilter } from '@/js/Mixins';
import { Contractor } from '@/js/FormObjects';
import VueSimpleSuggest from 'vue-simple-suggest';
import BaseSelect from '~/components/BaseSelect';
import BaseFileUploader from '~/components/BaseFileUploader';
import cloneDeep from 'lodash/fp/cloneDeep';

export default {
  name: 'Contractor',
  components: {
    BaseFileUploader,
    BaseSelect,
    BaseInput,
    ExplodedFieldset,
    VAddress,
    BottomNav,
    VueSimpleSuggest,
    BaseTimePicker,
    YesNo,
    BaseDatePicker,
    BaseTextarea,
    BaseCheckbox,
  },
  props: ['form', 'val', 'stateList'],
  mixins: [CapitalizeFilter],
  data() {
    return {
      contractors: [],
      partialFullDemoOptions: [
        { value: null, text: '' },
        { value: 'F', text: 'Full Demolition' },
        { value: 'P', text: 'Partial Demolition' },
      ],
      autosuggestProps: {
        id: 'contractor-suggest',
        onInputChange: this.inputChange,
      },
    };
  },
  mounted() {
    this.$emit('update:title', 'Project Information');
    this.contractors = this.$store.getters.contractors(this.form.projectType.toLowerCase());
    if (!this.contractors || this.contractors.length === 0) {
      const self = this;
      this.$emit('update:fetchingData', true);
      getContractors(this.form.projectType.toLowerCase())
        .then((response) => {
          self.contractors = response;
          self.$emit('update:fetchingData', false);
        })
        .catch(() => {
          self.$emit('update:fetchingData', false);
        });
    }
  },
  methods: {
    contractorSelected(contractor) {
      if (contractor && contractor.id) {
        // noinspection JSPrimitiveTypeWrapperUsage
        this.form.contractor = cloneDeep(contractor);
      }
    },
    // this is silly
    capitalize(lowercase) {
      return capitalize(lowercase);
    },
    clearContractor() {
      this.form.contractor = new Contractor('demolition');
    },
  },
  computed: {
    contractorReadonly() {
      return this.form.projectType.toLowerCase() === 'asbestos';
    },
    transporterReadonly() {
      return this.form.projectType.toLowerCase() === 'asbestos';
    },
    disposalSiteReadonly() {
      return this.form.projectType.toLowerCase() === 'asbestos';
    },
    isVariance() {
      return this.form.varianceCheckPublicHealth ||
        this.form.varianceCheckFinancialBurden ||
        this.form.varianceCheckCourtOrder;
    },
  },
};
</script>
